import { useCurrentRoute } from '@data/store/slices/route/getters'
import { getRouterMethod } from '@routes/utils/getRouterMethod'
import { normalizeUrlParams } from '@routes/utils/makeUrlWithParams'
import { nextRouterPromiseErrorHandler } from '@routes/utils/nextRouterPromiseErrorHandler'
import { RouterMethod } from '@routes/utils/types'
import { canCheckForContainerApp, isContainerApp } from '@util/device'
import { logError } from '@util/logger'
import { noop } from 'lodash-es'
import {
    // This is the only place in the entire repository that we should be importing useRouter!
    // eslint-disable-next-line no-restricted-imports
    useRouter,
} from 'next/compat/router'
import { useMemo, useRef } from 'react'

type NextCompatRouter = ReturnType<typeof useRouter>
type NonNullableRouter = NonNullable<NextCompatRouter>

export type InterNationsRouter = NextCompatRouter & {
    currentRoute: TRoute
    go: (
        route: TRoute,
        as?: Parameters<NonNullableRouter['push']>[1] & Parameters<NonNullableRouter['replace']>[1],
        transitionOptions?: Parameters<NonNullableRouter['push']>[2] & Parameters<NonNullableRouter['replace']>[2]
    ) => void
}

export function useNextRouterType(): 'pages' | 'app' {
    const compatRouter = useRouter()

    if (compatRouter === null) {
        return 'app'
    }

    return 'pages'
}

/**
 * Navigate directly to a route. Only for use with Pages Router.
 */
export function go({
    currentRoute,
    router,
    route,
    as,
    transitionOptions,
}: {
    currentRoute: TRoute
    router?: NextCompatRouter | null
    route: TRoute
    as?: Parameters<NonNullableRouter['push']>[1] & Parameters<NonNullableRouter['replace']>[1]
    transitionOptions?: Parameters<NonNullableRouter['push']>[2] & Parameters<NonNullableRouter['replace']>[2]
}): void {
    if (route.isINPOKEMON3273) {
        logError(`Called .go() on an INPOKEMON3273 route: ${route.id}`)
    }

    if (!route.isReady) {
        logError(`Cannot navigate to route ${route.id} because it has not yet received the required path arguments`)
        return
    }

    const method: RouterMethod = getRouterMethod({
        currentRoute,
        nextRoute: route,

        // These need to be passed to the custom function. If they are imported directly into the definition file, then it causes a circular dependency with Jest.
        isContainerApp: canCheckForContainerApp() && isContainerApp(),
    })

    if (method === 'full-page-load' || !router) {
        window.location.href = route.toString()

        return
    }

    const url = route.toURL()

    router[method](
        // We use an object instead of a string because NextRouter sometimes throws a "cancel rendering route"
        // error when a hash is used. It's difficult to reproduce, but does happen in the wild.
        // https://github.com/vercel/next.js/issues/37362
        {
            pathname: url.pathname,
            hash: url.hash,
            query: normalizeUrlParams(url.searchParams).toString(),
        },
        as,
        transitionOptions
    ).catch(nextRouterPromiseErrorHandler)

    return
}

/**
 * Creates a `go` function that has baked-in values for NextRouter and QueryClient
 */
function createGo(router: NextCompatRouter | null, currentRoute: TRoute) {
    /**
     * Navigate directly to a route
     */
    return function enhancedGo(
        route: TRoute,
        as?: Parameters<NonNullableRouter['push']>[1] & Parameters<NonNullableRouter['replace']>[1],
        transitionOptions?: Parameters<NonNullableRouter['push']>[2] & Parameters<NonNullableRouter['replace']>[2]
    ): void {
        return go({
            currentRoute,
            router,
            route,
            as,
            transitionOptions,
        })
    }
}

function useRefRouter(): NextCompatRouter {
    const router = useRouter()
    const ref = useRef(router)

    ref.current = router

    return ref.current
}

/**
 * For Pages Router only
 *
 * @deprecated Please switch to useINAppRouter as each page is moved to App Router
 */
export function useINRouter(): InterNationsRouter {
    const nextRouter = useRefRouter()
    const currentRoute = useCurrentRoute()
    const routerType = useNextRouterType()
    const enhancedRouter: InterNationsRouter = useMemo(
        () => {
            // Next's router is not yet ready, so return dummy values instead
            if (!nextRouter) {
                return {
                    asPath: '',
                    back: noop,
                    basePath: '',
                    beforePopState: noop,
                    currentRoute,
                    defaultLocale: '',
                    domainLocales: [],
                    events: {
                        emit: noop,
                        off: noop,
                        on: noop,
                    },
                    forward: noop,
                    go: (route) => {
                        const logMeta = { metadata: { route } }

                        if (!route.isReady) {
                            logError('Called go() before the route was ready', logMeta)
                        }

                        logError(
                            'Called go() before the NextRouter was ready. Will fall back to standard browser navigation.',
                            logMeta
                        )
                        window.location.href = route.toString()
                    },
                    isFallback: false,
                    isLocaleDomain: false,
                    isPreview: false,
                    isReady: routerType === 'app',
                    locale: '',
                    locales: [],
                    pathname: '',
                    prefetch: () => new Promise(noop),
                    push: () => new Promise(noop),
                    query: {},
                    reload: noop,
                    replace: () => new Promise(noop),
                    route: '',
                }
            }

            return {
                ...nextRouter,
                currentRoute,
                isReady: routerType === 'app' ? true : nextRouter.isReady,
                go: createGo(nextRouter, currentRoute),
            }
        },
        // For many properties of `nextRouter`, such as `push` and `replace` methods, we don't need "fresh" copies of
        // them. So we only include certain properties of `nextRouter` in the dependency array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            nextRouter?.asPath,
            nextRouter?.basePath,
            nextRouter?.isReady,
            nextRouter?.pathname,

            // We only care about *which* route is current, so its ID is a sufficient dependency.
            currentRoute.id,
            routerType,
        ]
    )

    return enhancedRouter
}
